/**
 * Einstiegspunkt der Anwendung.
 *
 * Verdrahtet Controller (Anwendungslogik) und Ansichten (UI). Der Zustand
 * lebt ausschliesslich im Controller-Objekt – es gibt keinen globalen
 * veraenderlichen Zustand.
 */

import './style.css';
import { EditorController } from './app/EditorController';
import { AudioTransport } from './audio/AudioTransport';
import { WebAudioEngine } from './audio/engine';
import { SampleLoader } from './audio/samples';
import { renderScoreFor } from './notation/renderSvg';
import { exportSvgAsPng, leadsheetFilename } from './export/pngExport';
import { GridView } from './ui/GridView';
import { LeadsheetControls } from './ui/LeadsheetControls';
import { NotationView } from './ui/NotationView';
import { TransportBar } from './ui/TransportBar';

function bootstrap(): void {
  const gridContainer = document.querySelector<HTMLElement>('#grid');
  const notationContainer = document.querySelector<HTMLElement>('#notation');
  const transportContainer = document.querySelector<HTMLElement>('#transport');
  const titleInput = document.querySelector<HTMLInputElement>('#title-input');
  const tempoInput = document.querySelector<HTMLInputElement>('#tempo-input');
  const exportButton =
    document.querySelector<HTMLButtonElement>('#export-button');
  const clearButton =
    document.querySelector<HTMLButtonElement>('#clear-button');

  if (
    !gridContainer ||
    !notationContainer ||
    !transportContainer ||
    !titleInput ||
    !tempoInput ||
    !exportButton ||
    !clearButton
  ) {
    throw new Error('Erwartete DOM-Elemente wurden nicht gefunden.');
  }

  const controller = EditorController.createFromStorage();

  const grid = new GridView(controller);
  grid.mount(gridContainer);

  const notation = new NotationView(controller);
  notation.mount(notationContainer);

  const controls = new LeadsheetControls(controller);
  controls.mount(titleInput, tempoInput);

  // Audiowiedergabe: Engine, Sample-Loader und Transport. Der Transport nutzt
  // stets das aktuelle Pattern und Tempo des Controllers.
  const engine = new WebAudioEngine();
  const transport = new AudioTransport({
    engine,
    loader: new SampleLoader(engine),
    getPattern: () => controller.snapshot().pattern,
    getBpm: () => controller.snapshot().meta.tempo,
  });
  const transportBar = new TransportBar(transport);
  transportBar.mount(transportContainer);
  // Sichtbare Abspielposition im Raster spiegeln (nur visuell).
  transport.subscribe((snapshot) => {
    grid.setPlayheadStep(snapshot.playheadStep);
  });

  exportButton.addEventListener('click', () => {
    const { pattern, meta } = controller.snapshot();
    const svg = renderScoreFor(pattern, meta);
    void exportSvgAsPng(svg, {
      filename: leadsheetFilename(meta.title),
    }).catch((error: unknown) => {
      console.error('PNG-Export fehlgeschlagen', error);
    });
  });

  clearButton.addEventListener('click', () => {
    controller.clear();
  });
}

bootstrap();
